import axios, { type AxiosError } from 'axios';

export const http = axios.create({ withCredentials: true });

export async function apiGet<T>(url: string): Promise<T> {
  return (await http.get<T>(url)).data;
}

export async function apiGetFresh<T>(url: string): Promise<T> {
  return (await http.get<T>(url, { headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' }, params: { _: Date.now() } })).data;
}
export async function apiPost<T>(url: string, body: unknown = {}): Promise<T> {
  return (await http.post<T>(url, body)).data;
}

export async function apiPostStream(
  url: string,
  body: unknown,
  onEvent: (event: Record<string, unknown>) => void,
  headers: Record<string, string> = {},
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify(body),
    signal,
  });
  if (!response.ok || !response.body) {
    const errorText = (await response.text().catch(() => '')).trim();
    throw new Error(errorText || `Stream failed (${response.status})`);
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed) onEvent(JSON.parse(trimmed) as Record<string, unknown>);
    }
  }
  const remaining = buffer.trim();
  if (remaining) onEvent(JSON.parse(remaining) as Record<string, unknown>);
}

export async function apiPostLive<T>(url: string, body: unknown, onLine: (line: string) => void, signal?: AbortSignal): Promise<T> {
  let result: T | undefined;
  let error = '';
  await apiPostStream(url, body, (event) => {
    if (event.type === 'log' && typeof event.line === 'string') onLine(event.line);
    if (event.type === 'result') result = event.body as T;
    if (event.type === 'error') error = String(event.error || 'That failed');
  }, { 'x-progress': '1' }, signal);
  if (error) throw new Error(error);
  if (result === undefined) throw new Error('That failed');
  return result;
}
export async function apiPut<T>(url: string, body: unknown = {}): Promise<T> {
  return (await http.put<T>(url, body)).data;
}
export async function apiDelete<T>(url: string): Promise<T> {
  return (await http.delete<T>(url)).data;
}
export function axiosData<T>(err: unknown): T | undefined {
  return (err as AxiosError<T>).response?.data;
}
